import { Fragment, useCallback, useEffect, useId, useMemo, useRef, useState, type FormEvent } from 'react'
import { api } from '../lib/api'
import { UAT_PREVIEW_ITEMS, UAT_PREVIEW_NOTES, UAT_PREVIEW_PROJECTS } from '../data/uat-preview'
import { UAT_QUESTIONS_PREVIEW } from '../data/response-plan-preview'
import {
  defaultUatItemType,
  formatFileSize,
  isUatCaseResult,
  isUatCustomerStatus,
  isUatItemType,
  isUatStatus,
  UAT_CASE_RESULT_LABEL,
  UAT_CASE_RESULTS,
  UAT_CUSTOMER_STATUSES,
  UAT_ITEM_TYPE_HINT,
  UAT_ITEM_TYPE_LABEL,
  UAT_ITEM_TYPES,
  UAT_LIMITS,
  UAT_ORDER_LABEL,
  UAT_REQUEST_TYPE_LABEL,
  UAT_REQUEST_TYPES,
  UAT_STATUS_LABEL,
  UAT_STATUS_SHORT,
  UAT_STATUS_TONE,
  UAT_STATUSES,
  uatCaseTally,
  uatNeedsCustomerAction,
  uatOrderProjects,
  uatProjectProgress,
  uatProjectSections,
  uatStatusCounts,
  uatTypeCounts,
  type UatCaseResult,
  type UatCustomerStatus,
  type UatItemType,
  type UatOrder,
  type UatRequestType,
  type UatStatus,
} from '../lib/uat'
import type { ThreadReply, UatCase, UatFile, UatHistoryEntry, UatItem, UatNote, UatProject, UatQuestion } from '../types'
import { parseUatHash, uatNoteHash, uatProjectChangesHash } from '../lib/uat-notes'
import { HowItShouldWork, latestPublishedDate, previewNoteComment, ReleaseNotesView } from './UatReleaseNotes'
import { Alert, Button, Dialog, EmptyState, Field, StatusBadge, useToast } from './ui'
import { Input, SectionHeader, Select } from './ui/primitives'
import { Chat, ChatAdd, ChatQuestion, ChevronDown, ClockCounterClockwise, DownloadSimple, File as FileIcon, Inbox, Plus, SpinnerGap } from './ui/icons'
import { formatThreadTime, ThreadReplies } from './ThreadReplies'
import { dateTime as formatUatTime } from '../lib/format'
import { formatDisplayDate } from './ui/date-logic'

type Filter = 'all' | UatStatus
type TypeFilter = 'all' | UatItemType
const ORDERS: UatOrder[] = ['test_now', 'project']

/** Which part of the tab shows: the items, or one project's What's Changing page. */
type TabView = { kind: 'items' } | { kind: 'notes'; projectId: string; noteId: string | null }

/** What the note dialog is for: a change to Needs Fix, or a failed case. */
type Prompt =
  | { kind: 'needs_fix'; item: UatItem }
  | { kind: 'fail'; item: UatItem; testCase: UatCase }

const PREVIEW_AUTHOR = 'Test Tester'
/** Items without a project, or with a project the viewer cannot see. */
const OTHER_GROUP = '__other__'

export { dateTime as formatUatTime } from '../lib/format'

function valueLabel(value: UatHistoryEntry['fromValue']): string {
  if (isUatStatus(value)) return UAT_STATUS_LABEL[value]
  if (isUatCaseResult(value)) return UAT_CASE_RESULT_LABEL[value]
  if (isUatItemType(value)) return UAT_ITEM_TYPE_LABEL[value]
  return ''
}

/** The type badge on a slim row, an admin row and an item body: Production Issue, UAT or Roadmap. */
export function UatTypeBadge({ type }: { type: UatItemType }) {
  return <span className={`uat-type-badge uat-type-badge--${type}`}><span className="ui-visually-hidden">Type: </span>{UAT_ITEM_TYPE_LABEL[type]}</span>
}

/** One sentence for a history entry, for example "Alison Johnson changed the status from In Testing to Needs Fix". */
export function historySentence(entry: UatHistoryEntry): string {
  if (entry.kind === 'note') return `${entry.authorName} commented`
  if (entry.kind === 'question') return `${entry.authorName} asked a question`
  if (entry.kind === 'section_comment') return `${entry.authorName} commented on What's Changing`
  if (entry.kind === 'request') return `${entry.authorName} added this item`
  if (entry.kind === 'merge') return `${entry.authorName} merged another item into this one`
  if (entry.kind === 'type') return `${entry.authorName} changed the type from ${valueLabel(entry.fromValue) || 'none'} to ${valueLabel(entry.toValue)}`
  if (entry.kind === 'case_result') return `${entry.authorName} marked a test case ${valueLabel(entry.toValue)}`
  if (entry.toValue === 'shipped') return entry.fromValue === 'needs_fix' ? `${entry.authorName} shipped the fix to your environment` : `${entry.authorName} shipped this to your environment`
  if (!entry.fromValue) return `${entry.authorName} added this item as ${valueLabel(entry.toValue)}`
  return `${entry.authorName} changed the status from ${valueLabel(entry.fromValue)} to ${valueLabel(entry.toValue)}`
}

/** The label above a comment: what the customer did. */
function commentLabel(entry: UatHistoryEntry): string | null {
  if (entry.kind === 'request') return entry.fromItemTitle ? 'Added an item' : 'Added this item'
  if (entry.kind === 'status') return 'Marked Needs Fix'
  if (entry.kind === 'case_result') return 'Marked a test case Fail'
  return null
}

/** Comments and their replies, for the slim row. */
export function commentCount(item: Pick<UatItem, 'history'>): number {
  return item.history.filter((entry) => entry.threaded).reduce((total, entry) => total + 1 + entry.replies.length, 0)
}

/** Local preview only: applies a change to the test data without a request. */
function previewChange(item: UatItem, change: { status?: UatStatus; note?: string | null; caseId?: string; result?: UatCaseResult }): UatItem {
  const now = new Date().toISOString()
  const base = { id: `preview-${Date.now()}`, authorName: PREVIEW_AUTHOR, authorSide: 'customer' as const, createdAt: now, replies: [] }
  if (change.caseId && change.result) {
    const testCase = item.cases.find((entry) => entry.id === change.caseId)
    if (!testCase) return item
    return {
      ...item,
      cases: item.cases.map((entry) => entry.id === change.caseId ? { ...entry, result: change.result!, note: change.note || null, resultBy: change.result === 'not_run' ? null : PREVIEW_AUTHOR, resultAt: change.result === 'not_run' ? null : now } : entry),
      history: [...item.history, { ...base, kind: 'case_result', body: change.note || null, fromValue: testCase.result, toValue: change.result, caseText: testCase.text, threaded: change.result === 'fail' }],
    }
  }
  if (change.status) {
    return { ...item, status: change.status, updatedAt: now, history: [...item.history, { ...base, kind: 'status', body: change.note || null, fromValue: item.status, toValue: change.status, caseText: null, threaded: change.status === 'needs_fix' }] }
  }
  return { ...item, history: [...item.history, { ...base, kind: 'note', body: change.note || null, fromValue: null, toValue: null, caseText: null, threaded: true }] }
}

/**
 * The UAT tab (Rod, 2026-11-04, v2 the same day): what Prentus ships to the
 * customer's environment, grouped by project, one slim row per item. A row
 * opens to the description, the test cases, the Comments thread and the
 * history. A verified customer adds items (Issue Found, Question or
 * Request), changes the status of a shipped item, marks test cases and
 * comments. Comments, Needs Fix, failed cases and added items go to the
 * Prentus team as Linear issues. `preview` (local fixtures only) uses test
 * data and makes no requests.
 */
export function UatTab({ publicId, preview = false }: { publicId: string; preview?: boolean }) {
  const toast = useToast()
  const [items, setItems] = useState<UatItem[]>(preview ? UAT_PREVIEW_ITEMS : [])
  const [projects, setProjects] = useState<UatProject[]>(preview ? UAT_PREVIEW_PROJECTS : [])
  const [customerLabel, setCustomerLabel] = useState(preview ? 'Summit State' : 'Your Team')
  const [canEdit, setCanEdit] = useState(preview)
  const [testData, setTestData] = useState(preview)
  const [state, setState] = useState<'loading' | 'ready' | 'error'>(preview ? 'ready' : 'loading')
  const [filter, setFilter] = useState<Filter>('all')
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all')
  // Test Now is the default view (Rod): what waits for the customer comes first.
  const [order, setOrder] = useState<UatOrder>('test_now')
  const [busy, setBusy] = useState<string | null>(null)
  const [prompt, setPrompt] = useState<Prompt | null>(null)
  const [adding, setAdding] = useState<{ projectId: string | null } | null>(null)
  const [questions, setQuestions] = useState<UatQuestion[]>(preview ? UAT_QUESTIONS_PREVIEW : [])
  const [thread, setThread] = useState({ canReply: preview, canModerate: false, assistantEnabled: preview })
  const [pending, setPending] = useState<Set<string>>(new Set())
  const [subscribed, setSubscribed] = useState<boolean | null>(preview ? true : null)
  const [notes, setNotes] = useState<UatNote[]>(preview ? UAT_PREVIEW_NOTES : [])
  const [view, setView] = useState<TabView>({ kind: 'items' })
  const [focusItem, setFocusItem] = useState<string | null>(null)

  const load = useCallback(async (quiet = false) => {
    if (preview) return
    if (!quiet) setState('loading')
    try {
      const data = await api.loadUat(publicId)
      setItems(data.items)
      setProjects(data.projects || [])
      setQuestions(data.questions || [])
      setNotes(data.notes || [])
      setCustomerLabel(data.customerLabel || 'Your Team')
      setCanEdit(data.canEdit)
      setThread({ canReply: Boolean(data.canReply), canModerate: Boolean(data.canModerate), assistantEnabled: Boolean(data.assistantEnabled) })
      setSubscribed(data.subscription ? data.subscription.subscribed : null)
      setTestData(Boolean(data.testData))
      setState('ready')
    } catch {
      if (!quiet) setState('error')
    }
  }, [preview, publicId])

  /** Waits for the Prentus Assistant's answer in one thread, then reloads quietly. */
  const awaitAssistant = useCallback(async (eventId: string | undefined, runId: string | null | undefined) => {
    if (!eventId || !runId) return
    setPending((current) => new Set(current).add(eventId))
    try {
      await api.runAssistant(publicId, runId)
    } catch {
      toast.info('The Answer Is Still Coming', { description: 'Refresh the page in a minute to see it.' })
    } finally {
      setPending((current) => { const next = new Set(current); next.delete(eventId); return next })
      await load(true)
    }
  }, [load, publicId, toast])

  function addPreviewReply(parentId: string, body: string): ThreadReply {
    const reply: ThreadReply = { id: `preview-${Date.now()}`, body, authorName: PREVIEW_AUTHOR, authorSide: 'customer', createdAt: new Date().toISOString(), corrected: false, sources: [] }
    setQuestions((current) => current.map((question) => question.id === parentId ? { ...question, replies: [...question.replies, reply] } : question))
    setItems((current) => current.map((item) => ({ ...item, history: item.history.map((entry) => entry.id === parentId ? { ...entry, replies: [...entry.replies, reply] } : entry) })))
    setNotes((current) => current.map((note) => ({ ...note, comments: note.comments.map((entry) => entry.id === parentId ? { ...entry, replies: [...entry.replies, reply] } : entry) })))
    return reply
  }

  async function reply(parentId: string, body: string): Promise<boolean> {
    if (preview) { addPreviewReply(parentId, body); return true }
    try {
      const { assistantRunId } = await api.sendReply(publicId, { type: 'uat-event', id: parentId }, body)
      toast.success('Reply Sent')
      await load(true)
      void awaitAssistant(parentId, assistantRunId)
      return true
    } catch (cause) {
      toast.error('Reply Not Sent', { description: cause instanceof Error ? cause.message : undefined })
      return false
    }
  }

  async function moderate(replyId: string, change: { body?: string; hidden?: boolean }): Promise<boolean> {
    if (preview) return true
    try {
      await api.moderateReply(publicId, replyId, change)
      toast.success(change.body !== undefined ? 'Answer Corrected' : change.hidden ? 'Answer Hidden' : 'Answer Shown')
      await load(true)
      return true
    } catch (cause) {
      toast.error('Answer Not Changed', { description: cause instanceof Error ? cause.message : undefined })
      return false
    }
  }

  async function ask(body: string): Promise<boolean> {
    if (preview) {
      setQuestions((current) => [{ id: `preview-${Date.now()}`, body, authorName: PREVIEW_AUTHOR, createdAt: new Date().toISOString(), replies: [] }, ...current])
      return true
    }
    try {
      const result = await api.askUatQuestion(publicId, body)
      setQuestions(result.questions)
      toast.success('Question Sent', { description: thread.assistantEnabled ? 'The Prentus Assistant is writing an answer.' : 'The Prentus team has it now.' })
      void awaitAssistant(result.eventId, result.assistantRunId)
      return true
    } catch (cause) {
      toast.error('Question Not Sent', { description: cause instanceof Error ? cause.message : undefined })
      return false
    }
  }

  async function addItem(input: { type: UatRequestType; itemType: UatItemType; title: string; details: string; projectId: string | null }): Promise<boolean> {
    if (preview) {
      const now = new Date().toISOString()
      const requests = UAT_PREVIEW_PROJECTS.find((project) => project.isRequests)
      setItems((current) => [...current, {
        id: `preview-${Date.now()}`, projectId: input.projectId || requests?.id || null, title: input.title, description: input.details, linearIdentifier: null, deliveredOn: null, targetDate: null, status: 'new', itemType: input.itemType, addedByCustomer: true, requestType: input.type, updatedAt: now, cases: [], files: [],
        history: [{ id: `preview-${Date.now()}-r`, kind: 'request', authorName: PREVIEW_AUTHOR, authorSide: 'customer', body: input.details || null, fromValue: null, toValue: null, caseText: null, createdAt: now, replies: [], threaded: true }],
      }])
      return true
    }
    try {
      const result = await api.addCustomerUatItem(publicId, input)
      setProjects(result.projects)
      setItems((current) => [...current, result.item])
      toast.success('Item Added', { description: 'The Prentus team has it now. It shows as New until they plan it.' })
      void awaitAssistant(result.eventId, result.assistantRunId)
      return true
    } catch (cause) {
      toast.error('Item Not Added', { description: cause instanceof Error ? cause.message : undefined })
      return false
    }
  }

  async function changeSubscription(next: boolean) {
    if (preview) { setSubscribed(next); return }
    setSubscribed(next)
    try {
      await api.setUatSubscription(publicId, next)
      toast.success(next ? 'Ship Emails On' : 'Ship Emails Off')
    } catch (cause) {
      setSubscribed(!next)
      toast.error('Email Setting Not Saved', { description: cause instanceof Error ? cause.message : undefined })
    }
  }

  async function commentOnNote(note: UatNote, body: string): Promise<boolean> {
    if (preview) {
      setNotes((current) => current.map((entry) => entry.id === note.id ? previewNoteComment(entry, body, PREVIEW_AUTHOR) : entry))
      toast.success('Comment Sent')
      return true
    }
    try {
      const result = await api.addUatNoteComment(publicId, note.id, body)
      if (result.note) setNotes((current) => current.map((entry) => entry.id === note.id ? result.note! : entry))
      toast.success('Comment Sent', { description: thread.assistantEnabled ? 'The Prentus Assistant is writing an answer.' : 'The Prentus team has it now.' })
      void awaitAssistant(result.eventId, result.assistantRunId)
      return true
    } catch (cause) {
      toast.error('Comment Not Sent', { description: cause instanceof Error ? cause.message : undefined })
      return false
    }
  }

  /** Opens a project's What's Changing page, at one section when given, and keeps the place in the link. */
  function openNotes(projectId: string, noteId: string | null = null) {
    setView({ kind: 'notes', projectId, noteId })
    window.history.replaceState(null, '', noteId ? uatNoteHash(noteId) : uatProjectChangesHash(projectId))
    if (!noteId) window.scrollTo({ top: 0 })
  }

  function openItem(itemId: string) {
    setView({ kind: 'items' })
    setFilter('all')
    setTypeFilter('all')
    setFocusItem(itemId)
    window.history.replaceState(null, '', `#uat/item/${itemId}`)
  }

  function backToItems() {
    setView({ kind: 'items' })
    window.history.replaceState(null, '', '#uat')
  }

  // Links such as "#uat/changes/<id>" (an Assistant source, a shared link) open the right place.
  useEffect(() => {
    if (state !== 'ready') return
    function apply() {
      const target = parseUatHash(window.location.hash)
      if (!target) return
      if (target.kind === 'note') {
        const note = notes.find((entry) => entry.id === target.id)
        if (note) setView({ kind: 'notes', projectId: note.projectId, noteId: note.id })
      } else if (target.kind === 'project') {
        if (notes.some((entry) => entry.projectId === target.id)) setView({ kind: 'notes', projectId: target.id, noteId: null })
      } else if (items.some((item) => item.id === target.id)) {
        setView({ kind: 'items' })
        setFocusItem(target.id)
      }
    }
    apply()
    window.addEventListener('hashchange', apply)
    return () => window.removeEventListener('hashchange', apply)
  }, [state, notes, items])

  const threadProps: ThreadProps = { canReply: thread.canReply, canModerate: thread.canModerate, pending, onReply: reply, onModerate: moderate }
  useEffect(() => { void load() }, [load])
  const notesByProject = useMemo(() => {
    const map = new Map<string, UatNote[]>()
    for (const note of notes) map.set(note.projectId, [...(map.get(note.projectId) || []), note])
    return map
  }, [notes])
  const notesByItem = useMemo(() => {
    const map = new Map<string, UatNote[]>()
    for (const note of notes) for (const itemId of note.itemIds) map.set(itemId, [...(map.get(itemId) || []), note])
    return map
  }, [notes])

  // The status and type filters work together. Each chip counts the items that the other filter leaves.
  const statusMatch = useCallback((item: UatItem) => filter === 'all' || item.status === filter, [filter])
  const typeMatch = useCallback((item: UatItem) => typeFilter === 'all' || item.itemType === typeFilter, [typeFilter])
  const counts = useMemo(() => uatStatusCounts(items.filter(typeMatch)), [items, typeMatch])
  const typeCounts = useMemo(() => uatTypeCounts(items.filter(statusMatch)), [items, statusMatch])
  // The active status chip stays, also when the type filter leaves it no items, so the customer can turn it off.
  const filters = useMemo(() => UAT_STATUSES.filter((status) => status === filter || counts[status] > 0 || ['planned', 'shipped', 'in_testing', 'passed', 'needs_fix'].includes(status)), [counts, filter])
  const filtered = filter !== 'all' || typeFilter !== 'all'
  const shown = items.filter((item) => statusMatch(item) && typeMatch(item))
  const groups = useMemo(() => {
    const known = new Set(projects.map((project) => project.id))
    const list = projects.map((project) => ({ key: project.id, project, items: items.filter((item) => item.projectId === project.id) }))
    const other = items.filter((item) => !item.projectId || !known.has(item.projectId))
    if (other.length) list.push({ key: OTHER_GROUP, project: { id: OTHER_GROUP, name: 'Other Items', description: 'Items that are not in a project yet.', targetDate: null, isRequests: false, files: [] }, items: other })
    return uatOrderProjects(list, order)
  }, [items, projects, order])
  const filterLabel = [filter !== 'all' ? UAT_STATUS_SHORT[filter] : null, typeFilter !== 'all' ? UAT_ITEM_TYPE_LABEL[typeFilter] : null].filter(Boolean).join(' ')
  const statusGroupId = useId()
  const typeGroupId = useId()
  const orderGroupId = useId()
  function clearFilters() { setFilter('all'); setTypeFilter('all') }

  function replace(next: UatItem) {
    setItems((current) => current.map((item) => item.id === next.id ? next : item))
  }

  async function run(key: string, previewNext: () => UatItem, request: () => Promise<{ item: UatItem }>, success: string, failure: string): Promise<boolean> {
    setBusy(key)
    try {
      replace(preview ? previewNext() : (await request()).item)
      toast.success(success)
      return true
    } catch (cause) {
      toast.error(failure, { description: cause instanceof Error ? cause.message : undefined })
      return false
    } finally {
      setBusy(null)
    }
  }

  function changeStatus(item: UatItem, status: UatCustomerStatus, note?: string) {
    return run(item.id, () => previewChange(item, { status, note }), () => api.setUatStatus(publicId, item.id, status, note), `Status Changed to ${UAT_STATUS_SHORT[status]}`, 'Status Not Changed')
  }

  function comment(item: UatItem, note: string) {
    return run(item.id, () => previewChange(item, { note }), async () => {
      const result = await api.addUatNote(publicId, item.id, note)
      void awaitAssistant(result.eventId, result.assistantRunId)
      return result
    }, 'Comment Sent', 'Comment Not Sent')
  }

  function setResult(item: UatItem, testCase: UatCase, result: UatCaseResult, note?: string) {
    // Keyed by item: each response replaces the whole item, so one request per item at a time.
    return run(item.id, () => previewChange(item, { caseId: testCase.id, result, note }), () => api.setUatCaseResult(publicId, testCase.id, result, note), `Test Case Marked ${UAT_CASE_RESULT_LABEL[result]}`, 'Result Not Saved')
  }

  async function submitPrompt(note: string): Promise<boolean> {
    if (!prompt) return false
    const done = prompt.kind === 'needs_fix'
      ? await changeStatus(prompt.item, 'needs_fix', note || undefined)
      : await setResult(prompt.item, prompt.testCase, 'fail', note || undefined)
    if (done) setPrompt(null)
    return done
  }

  const rowProps = {
    canEdit,
    busy,
    thread: threadProps,
    customerLabel,
    focusItem,
    notesByItem,
    onOpenNote: (note: UatNote) => openNotes(note.projectId, note.id),
    onStatus: (item: UatItem, status: UatCustomerStatus) => { if (status === 'needs_fix') setPrompt({ kind: 'needs_fix', item }); else void changeStatus(item, status) },
    onComment: comment,
    onResult: (item: UatItem, testCase: UatCase, result: UatCaseResult) => { if (result === 'fail') setPrompt({ kind: 'fail', item, testCase }); else void setResult(item, testCase, result) },
  }

  return (
    <section className="standard-tab uat-tab" aria-busy={state === 'loading' || undefined}>
      <div className="uat-tab-head">
        <SectionHeader title="UAT" description="What Prentus ships to your environment, by project. Open an item to test it, mark each test case and comment. Comments, Needs Fix, failed cases and items you add go straight to the Prentus team." />
        {state === 'ready' && canEdit && <Button type="button" onClick={() => setAdding({ projectId: null })}><Plus size={16} aria-hidden="true" />Add Item</Button>}
      </div>
      {testData && <Alert tone="warning" title="Preview With Test Data" description="This is a review page, not a customer workspace. Every project, item, test case, file and person here is test data." />}
      {!testData && state === 'ready' && !canEdit && <Alert tone="info" title="Customer View" description="Only a signed-in customer can add an item, change a status, mark a test case or comment. You can reply in comment threads. You see what they see." />}
      {state === 'loading' && <div className="loading-lockup" role="status"><SpinnerGap size={18} className="spin" aria-hidden="true" />Loading UAT items</div>}
      {state === 'error' && <Alert tone="danger" title="UAT Items Did Not Load" description="Check your connection and try again." action={<Button type="button" variant="secondary" compact onClick={() => void load()}>Try Again</Button>} />}
      {state === 'ready' && view.kind === 'notes' && (() => {
        const project = projects.find((entry) => entry.id === view.projectId)
        const projectNotes = notesByProject.get(view.projectId) || []
        if (!project || !projectNotes.length) return <Alert tone="info" title="Section Not Found" description="This What's Changing section is not available. It may have moved." action={<Button type="button" variant="secondary" compact onClick={backToItems}>Back to UAT Items</Button>} />
        return (
          <ReleaseNotesView
            project={project}
            notes={projectNotes}
            items={items}
            customerLabel={customerLabel}
            canEdit={canEdit}
            thread={threadProps}
            focusNoteId={view.noteId}
            onBack={backToItems}
            onOpenItem={openItem}
            onFocusNote={(noteId) => openNotes(project.id, noteId)}
            onComment={commentOnNote}
          />
        )
      })()}
      {state === 'ready' && view.kind === 'items' && (
        <>
          <div className="uat-toolbar">
            <div className="uat-filter-rows">
              <div className="uat-filter-row">
                <span className="uat-filter-label" id={statusGroupId}>Status</span>
                <div className="uat-filters" role="group" aria-labelledby={statusGroupId}>
                  <button type="button" className={`uat-filter${filter === 'all' ? ' is-active' : ''}`} aria-pressed={filter === 'all'} onClick={() => setFilter('all')}>All <span>{items.filter(typeMatch).length}</span></button>
                  {filters.map((status) => (
                    <button type="button" key={status} className={`uat-filter uat-filter--${status}${filter === status ? ' is-active' : ''}`} aria-pressed={filter === status} onClick={() => setFilter(status)}>
                      {UAT_STATUS_SHORT[status]} <span>{counts[status]}</span>
                    </button>
                  ))}
                </div>
              </div>
              <div className="uat-filter-row">
                <span className="uat-filter-label" id={typeGroupId}>Type</span>
                <div className="uat-filters" role="group" aria-labelledby={typeGroupId}>
                  <button type="button" className={`uat-filter${typeFilter === 'all' ? ' is-active' : ''}`} aria-pressed={typeFilter === 'all'} onClick={() => setTypeFilter('all')}>All <span>{items.filter(statusMatch).length}</span></button>
                  {UAT_ITEM_TYPES.map((type) => (
                    <button type="button" key={type} className={`uat-filter uat-filter--type-${type}${typeFilter === type ? ' is-active' : ''}`} aria-pressed={typeFilter === type} title={UAT_ITEM_TYPE_HINT[type]} onClick={() => setTypeFilter(type)}>
                      {UAT_ITEM_TYPE_LABEL[type]} <span>{typeCounts[type]}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="uat-toolbar-side">
              <div className="uat-order">
                <span className="uat-filter-label" id={orderGroupId}>Order</span>
                <div className="uat-order-options" role="group" aria-labelledby={orderGroupId}>
                  {ORDERS.map((option) => (
                    <button type="button" key={option} className={`uat-order-option${order === option ? ' is-selected' : ''}`} aria-pressed={order === option} onClick={() => setOrder(option)}>{UAT_ORDER_LABEL[option]}</button>
                  ))}
                </div>
              </div>
              {subscribed !== null && (
                <label className="option-toggle uat-email-toggle">
                  <input type="checkbox" checked={subscribed} onChange={(event) => void changeSubscription(event.target.checked)} />
                  <span>Email Me When Items Ship</span>
                </label>
              )}
            </div>
          </div>
          {order === 'test_now' && <p className="uat-order-hint">Test Now puts first what waits for your team: items Shipped to Your Environment or In Testing, and open production issues.{typeFilter === 'all' ? ' Roadmap items are in a Roadmap group in each project.' : ''}</p>}
          <p className="ui-visually-hidden" role="status">{filtered ? `Showing ${shown.length} ${filterLabel} items.` : `Showing all ${items.length} items.`}</p>
          {items.length === 0 && groups.length === 0
            ? <EmptyState icon={Inbox} title="No UAT Items Yet" description={canEdit ? 'Items show here when Prentus ships them to your environment. Select Add Item to report an issue, ask a question or make a request.' : 'Items show here when Prentus ships them to the customer environment.'} />
            : filtered && shown.length === 0
              ? <EmptyState icon={Inbox} title={`No ${filterLabel} Items`} description="Choose another status or type to see more items." action={<Button type="button" variant="secondary" compact onClick={clearFilters}>Show All Items</Button>} />
              : (
                <div className="uat-projects">
                  {groups.map((group, index) => {
                    const visible = group.items.filter((item) => statusMatch(item) && typeMatch(item))
                    if (filtered && !visible.length) return null
                    const waiting = order === 'test_now' && visible.some(uatNeedsCustomerAction)
                    // A light rule under the pinned Your Requests project, before the releases (Will, 2026-11-04).
                    const divider = group.project.isRequests && groups.slice(index + 1).some((next) => !filtered || next.items.some((item) => statusMatch(item) && typeMatch(item)))
                    return (
                      <Fragment key={`${group.key}-${filter}-${typeFilter}-${order}`}>
                        <ProjectGroup
                          project={group.project}
                          items={group.items}
                          visible={visible}
                          order={order}
                          collapseRoadmap={typeFilter === 'all'}
                          notes={notesByProject.get(group.key) || []}
                          onOpenNotes={() => openNotes(group.project.id)}
                          startOpen={filtered || waiting || groups.length === 1 || Boolean(focusItem && group.items.some((item) => item.id === focusItem))}
                          canAdd={canEdit && group.key !== OTHER_GROUP}
                          onAdd={() => setAdding({ projectId: group.project.id })}
                          rowProps={rowProps}
                        />
                        {divider && <hr className="uat-projects-divider" />}
                      </Fragment>
                    )
                  })}
                </div>
              )}
          <AskQuestion canAsk={canEdit} assistantEnabled={thread.assistantEnabled} questions={questions} onAsk={ask} {...threadProps} />
        </>
      )}
      <NoteDialog prompt={prompt} busy={busy !== null} onClose={() => setPrompt(null)} onSubmit={submitPrompt} />
      <AddItemDialog open={adding} projects={projects} items={items} customerLabel={customerLabel} onClose={() => setAdding(null)} onSubmit={async (input) => { const done = await addItem(input); if (done) setAdding(null); return done }} />
    </section>
  )
}

type ThreadProps = {
  canReply: boolean
  canModerate: boolean
  pending: Set<string>
  onReply: (parentId: string, body: string) => Promise<boolean>
  onModerate: (replyId: string, change: { body?: string; hidden?: boolean }) => Promise<boolean>
}

type RowProps = {
  canEdit: boolean
  busy: string | null
  thread: ThreadProps
  customerLabel: string
  /** An item to open and scroll to (from a What's Changing section or a link). */
  focusItem: string | null
  notesByItem: Map<string, UatNote[]>
  onOpenNote: (note: UatNote) => void
  onStatus: (item: UatItem, status: UatCustomerStatus) => void
  onComment: (item: UatItem, note: string) => Promise<boolean>
  onResult: (item: UatItem, testCase: UatCase, result: UatCaseResult) => void
}

/**
 * The Ask a Question box (Rod, 2026-11-04). A question goes to the Prentus
 * team like a comment. When the Prentus Assistant is on, it answers in the
 * question's thread right away.
 */
function AskQuestion({ canAsk, assistantEnabled, questions, onAsk, ...thread }: ThreadProps & { canAsk: boolean; assistantEnabled: boolean; questions: UatQuestion[]; onAsk: (body: string) => Promise<boolean> }) {
  const [text, setText] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [open, setOpen] = useState(false)
  const listId = useId()
  const trimmed = text.trim()
  async function submit(event: FormEvent) {
    event.preventDefault()
    if (busy) return
    if (!trimmed) { setError('Write a question before you send it.'); return }
    if (trimmed.length > UAT_LIMITS.noteChars) { setError(`Keep the question under ${UAT_LIMITS.noteChars.toLocaleString('en-US')} characters.`); return }
    setError('')
    setBusy(true)
    const sent = await onAsk(trimmed)
    setBusy(false)
    if (sent) { setText(''); setOpen(true) }
  }
  return (
    <section className="uat-ask" aria-labelledby={`${listId}-title`}>
      <div className="uat-ask-head">
        <span className="uat-ask-icon" aria-hidden="true"><ChatQuestion size={20} /></span>
        <div>
          <h3 id={`${listId}-title`}>Ask a Question</h3>
          <p>{assistantEnabled ? 'Ask how something should work. The Prentus Assistant, an AI, answers right away from the feature notes and our QA guides. The Prentus team reads every question. For one item, open it and use Comment.' : 'Ask how something should work. The Prentus team answers here. For one item, open it and use Comment.'}</p>
        </div>
      </div>
      {canAsk && (
        <form className="uat-ask-form" onSubmit={(event) => void submit(event)} noValidate>
          <Field label="Your Question" error={error || undefined} hint={`${trimmed.length.toLocaleString('en-US')} of ${UAT_LIMITS.noteChars.toLocaleString('en-US')} characters`}>
            <textarea value={text} rows={2} maxLength={UAT_LIMITS.noteChars} placeholder="For example: should a saved filter keep the sort order?" onChange={(event) => setText(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) void submit(event) }} />
          </Field>
          <div className="thread-form-actions"><Button type="submit" compact disabled={busy}>{busy && <SpinnerGap size={14} className="spin" aria-hidden="true" />}Ask Question</Button></div>
        </form>
      )}
      {questions.length > 0 && (
        <div className="uat-disclosure">
          <button type="button" className="uat-disclosure-toggle" aria-expanded={open} aria-controls={listId} onClick={() => setOpen((value) => !value)}>
            <ChevronDown size={16} aria-hidden="true" className={open ? 'is-open' : ''} />
            Questions ({questions.length})
          </button>
          {open && (
            <ol id={listId} className="uat-questions">
              {questions.map((question) => (
                <li key={question.id} className="uat-question">
                  <p className="plan-note-head"><strong>{question.authorName}</strong> <time dateTime={question.createdAt}>{formatThreadTime(question.createdAt)}</time></p>
                  <p className="plan-note-body">{question.body}</p>
                  <ThreadReplies replies={question.replies} label={`${question.authorName}'s question`} canReply={thread.canReply} canModerate={thread.canModerate} assistantPending={thread.pending.has(question.id)} onReply={(body) => thread.onReply(question.id, body)} onModerate={thread.onModerate} />
                </li>
              ))}
            </ol>
          )}
        </div>
      )}
    </section>
  )
}

/** A download in a project header or an item: file name, version, size, the admin's note and a clear Download button. */
function FileDownload({ file }: { file: UatFile }) {
  const details = [file.versionLabel ? `Version ${file.versionLabel}` : null, formatFileSize(file.byteSize), file.fileName].filter(Boolean).join(' · ')
  return (
    <div className="uat-download">
      <span className="uat-download-icon" aria-hidden="true"><FileIcon size={18} /></span>
      <div className="uat-download-text">
        <strong>{file.title}</strong>
        <small>{details}</small>
        {file.note && <p>{file.note}</p>}
      </div>
      <a className="button button-primary compact uat-download-button" href={file.downloadUrl} download={file.fileName}>
        <DownloadSimple size={16} aria-hidden="true" />Download<span className="ui-visually-hidden"> {file.title}</span>
      </a>
    </div>
  )
}

function ProjectGroup({ project, items, visible, order, collapseRoadmap, notes, onOpenNotes, startOpen, canAdd, onAdd, rowProps }: {
  project: UatProject
  items: UatItem[]
  visible: UatItem[]
  order: UatOrder
  /** Roadmap items sit in a collapsed "Roadmap (N)" group, unless the customer filters by type. */
  collapseRoadmap: boolean
  /** Published What's Changing sections of the project. */
  notes: UatNote[]
  onOpenNotes: () => void
  startOpen: boolean
  canAdd: boolean
  onAdd: () => void
  rowProps: RowProps
}) {
  const [open, setOpen] = useState(startOpen)
  useEffect(() => { if (startOpen) setOpen(true) }, [startOpen])
  const bodyId = useId()
  const headingId = useId()
  const progress = uatProjectProgress(items)
  const published = latestPublishedDate(notes)
  const percent = progress.total ? Math.round((progress.passed / progress.total) * 100) : 0
  // Each item keeps the part it had when the group opened (the group remounts on a filter or order change).
  // A status change then does not move an open row to another list, where it would remount closed and lose focus.
  const placed = useRef(new Map<string, 'testNow' | 'others' | 'roadmap'>())
  const computed = uatProjectSections(visible, { order, collapseRoadmap })
  for (const part of ['testNow', 'others', 'roadmap'] as const) for (const item of computed[part]) if (!placed.current.has(item.id)) placed.current.set(item.id, part)
  const sections = {
    // Open Production Issues first, then the project order, as in uatProjectSections. The order does not move when a status changes.
    testNow: visible.filter((item) => placed.current.get(item.id) === 'testNow').sort((a, b) => Number(b.itemType === 'production') - Number(a.itemType === 'production')),
    others: visible.filter((item) => placed.current.get(item.id) === 'others'),
    roadmap: visible.filter((item) => placed.current.get(item.id) === 'roadmap'),
  }
  const roadmapId = useId()
  const roadmapHasFocus = Boolean(rowProps.focusItem && sections.roadmap.some((item) => item.id === rowProps.focusItem))
  const [roadmapOpen, setRoadmapOpen] = useState(roadmapHasFocus)
  useEffect(() => { if (roadmapHasFocus) setRoadmapOpen(true) }, [roadmapHasFocus])
  return (
    <section className={`uat-project${open ? ' is-open' : ''}`} aria-labelledby={headingId}>
      <div className="uat-project-head">
        <div className="uat-project-title">
          <h3 id={headingId}>
            <button type="button" className="uat-project-toggle" aria-expanded={open} aria-controls={bodyId} onClick={() => setOpen((value) => !value)}>
              <ChevronDown size={18} aria-hidden="true" className={open ? 'is-open' : ''} />
              <span>{project.name}</span>
            </button>
          </h3>
          {project.description && <p className="uat-project-description">{project.description}</p>}
        </div>
        <div className="uat-project-meta">
          {order === 'test_now' && sections.testNow.length > 0 && <span className="uat-project-waiting">{sections.testNow.length} to Test</span>}
          {project.targetDate && <span className="uat-project-date">Target {formatDisplayDate(project.targetDate)}</span>}
          <span className="uat-project-progress">
            <span>{progress.label}</span>
            {progress.total > 0 && <span className="uat-progress-bar" aria-hidden="true"><span style={{ width: `${percent}%` }} /></span>}
          </span>
          {canAdd && <Button type="button" variant="secondary" compact onClick={onAdd} aria-label={`Add an item to ${project.name}`}><Plus size={14} aria-hidden="true" />Add Item</Button>}
        </div>
      </div>
      {notes.length > 0 && (
        <div className="uat-project-changes">
          <span className="uat-project-changes-icon" aria-hidden="true"><FileIcon size={18} /></span>
          <div className="uat-project-changes-text">
            <strong>What's Changing</strong>
            <small>How {project.name} should work, in {notes.length} {notes.length === 1 ? 'section' : 'sections'}{published ? `. Published ${published}` : ''}.</small>
          </div>
          <Button type="button" compact onClick={onOpenNotes} aria-label={`Read What's Changing for ${project.name}`}>Read What's Changing</Button>
        </div>
      )}
      {project.files.length > 0 && (
        <div className="uat-downloads">{project.files.map((file) => <FileDownload key={file.id} file={file} />)}</div>
      )}
      {open && (
        <div id={bodyId} className="uat-project-body">
          {visible.length === 0 && <p className="uat-project-empty">{canAdd ? 'No items in this project yet. Select Add Item to report an issue, ask a question or make a request.' : 'No items in this project yet.'}</p>}
          {sections.testNow.length > 0 && (
            <>
              <p className="uat-section-label"><strong>Test Now</strong> <span>{sections.testNow.length}</span><small>Waiting for your team</small></p>
              <ul className="uat-rows">{sections.testNow.map((item) => <UatRow key={item.id} item={item} {...rowProps} />)}</ul>
            </>
          )}
          {sections.others.length > 0 && (
            <>
              {sections.testNow.length > 0 && <p className="uat-section-label"><strong>Everything Else</strong> <span>{sections.others.length}</span></p>}
              <ul className="uat-rows">{sections.others.map((item) => <UatRow key={item.id} item={item} {...rowProps} />)}</ul>
            </>
          )}
          {sections.roadmap.length > 0 && (
            <div className="uat-roadmap">
              <div className="uat-roadmap-head">
                <button type="button" className="uat-disclosure-toggle" aria-expanded={roadmapOpen} aria-controls={roadmapId} onClick={() => setRoadmapOpen((value) => !value)}>
                  <ChevronDown size={16} aria-hidden="true" className={roadmapOpen ? 'is-open' : ''} />
                  Roadmap ({sections.roadmap.length})
                </button>
                <small>{UAT_ITEM_TYPE_HINT.roadmap}.</small>
              </div>
              {roadmapOpen && <ul id={roadmapId} className="uat-rows">{sections.roadmap.map((item) => <UatRow key={item.id} item={item} {...rowProps} />)}</ul>}
            </div>
          )}
        </div>
      )}
    </section>
  )
}

function UatRow({ item, canEdit, busy, thread, customerLabel, focusItem, notesByItem, onOpenNote, onStatus, onComment, onResult }: RowProps & { item: UatItem }) {
  const bodyId = useId()
  const titleId = useId()
  const rowRef = useRef<HTMLLIElement>(null)
  const waiting = item.history.some((entry) => thread.pending.has(entry.id))
  const [open, setOpen] = useState(false)
  // A thread that waits for an Assistant answer opens the row, so the answer shows where it lands.
  useEffect(() => { if (waiting) setOpen(true) }, [waiting])
  // An item opened from a What's Changing section opens and scrolls into view.
  const focused = focusItem === item.id
  useEffect(() => {
    if (!focused) return
    setOpen(true)
    const frame = window.requestAnimationFrame(() => {
      rowRef.current?.scrollIntoView({ block: 'start' })
      rowRef.current?.querySelector<HTMLButtonElement>('.uat-row-toggle')?.focus({ preventScroll: true })
    })
    return () => window.cancelAnimationFrame(frame)
  }, [focused])
  const tally = uatCaseTally(item.cases)
  const comments = commentCount(item)
  return (
    <li ref={rowRef} className={`uat-row${open ? ' is-open' : ''}`}>
      <button type="button" className="uat-row-toggle" aria-expanded={open} aria-controls={bodyId} onClick={() => setOpen((value) => !value)}>
        <ChevronDown size={16} aria-hidden="true" className={open ? 'is-open' : ''} />
        <span className="uat-row-badges">
          <StatusBadge tone={UAT_STATUS_TONE[item.status]}>{UAT_STATUS_SHORT[item.status]}</StatusBadge>
          <UatTypeBadge type={item.itemType} />
        </span>
        <span className="uat-row-title" id={titleId}>{item.title}</span>
        <span className="uat-row-meta">
          {item.addedByCustomer && <span className="uat-added-badge">Added by {customerLabel}</span>}
          {item.linearIdentifier && <span className="uat-row-ticket">{item.linearIdentifier}</span>}
          {tally && <span>{tally}</span>}
          <span className="uat-row-comments"><Chat size={14} aria-hidden="true" />{comments}<span className="ui-visually-hidden"> {comments === 1 ? 'comment' : 'comments'}</span></span>
        </span>
      </button>
      {open && <UatItemBody id={bodyId} titleId={titleId} item={item} canEdit={canEdit} busy={busy} thread={thread} customerLabel={customerLabel} focusItem={focusItem} notesByItem={notesByItem} onOpenNote={onOpenNote} onStatus={onStatus} onComment={onComment} onResult={onResult} />}
    </li>
  )
}

function UatItemBody({ id, titleId, item, canEdit, busy, thread, customerLabel, notesByItem, onOpenNote, onStatus, onComment, onResult }: RowProps & { id: string; titleId: string; item: UatItem }) {
  const historyId = useId()
  const commentFieldId = useId()
  const [historyOpen, setHistoryOpen] = useState(false)
  const [composing, setComposing] = useState(false)
  const [text, setText] = useState('')
  const [error, setError] = useState('')
  // The select only picks a status; Save Status sends it (WCAG 3.2.2: no change on input).
  const [draftStatus, setDraftStatus] = useState<UatCustomerStatus>(isUatCustomerStatus(item.status) ? item.status : 'in_testing')
  useEffect(() => { if (isUatCustomerStatus(item.status)) setDraftStatus(item.status) }, [item.status])
  const itemBusy = busy === item.id
  const threads = item.history.filter((entry) => entry.threaded)
  const others = item.history.filter((entry) => !entry.threaded)
  const testable = isUatCustomerStatus(item.status)
  const trimmed = text.trim()

  async function sendComment(event: FormEvent) {
    event.preventDefault()
    if (itemBusy) return
    if (!trimmed) { setError('Write a comment before you send it.'); return }
    if (trimmed.length > UAT_LIMITS.noteChars) { setError(`Keep the comment under ${UAT_LIMITS.noteChars.toLocaleString('en-US')} characters.`); return }
    setError('')
    if (await onComment(item, trimmed)) { setText(''); setComposing(false) }
  }

  const dates = [
    item.deliveredOn && item.status !== 'planned' && item.status !== 'new' ? `Shipped to your environment ${formatDisplayDate(item.deliveredOn)}` : null,
    item.targetDate && (item.status === 'planned' || item.status === 'new') ? `Target ${formatDisplayDate(item.targetDate)}` : null,
    item.addedByCustomer ? `Added by ${customerLabel}${item.requestType ? ` as ${UAT_REQUEST_TYPE_LABEL[item.requestType]}` : ''}` : null,
    item.linearIdentifier ? `Ticket ${item.linearIdentifier}` : null,
  ].filter(Boolean)

  return (
    <div id={id} className="uat-row-body" role="region" aria-labelledby={titleId}>
      <p className="uat-item-type"><UatTypeBadge type={item.itemType} /><span>{UAT_ITEM_TYPE_HINT[item.itemType]}.</span></p>
      {dates.length > 0 && <p className="uat-item-meta">{dates.map((entry) => <span key={entry}>{entry}</span>)}</p>}
      {item.description && <p className="uat-item-description">{item.description}</p>}
      <HowItShouldWork notes={notesByItem.get(item.id) || []} onOpen={onOpenNote} />
      {item.files.length > 0 && <div className="uat-downloads">{item.files.map((file) => <FileDownload key={file.id} file={file} />)}</div>}

      {canEdit && (testable
        ? (
          <div className="uat-item-actions">
            <Field label="Status" className="uat-status-field">
              <Select value={draftStatus} disabled={itemBusy} onChange={(event) => setDraftStatus(event.target.value as UatCustomerStatus)}>
                {UAT_CUSTOMER_STATUSES.map((status) => <option key={status} value={status}>{UAT_STATUS_LABEL[status]}</option>)}
              </Select>
            </Field>
            {draftStatus !== item.status && <Button type="button" compact disabled={itemBusy} onClick={() => onStatus(item, draftStatus)}>Save Status</Button>}
            {itemBusy && <SpinnerGap size={16} className="spin" aria-label="Saving" />}
          </div>
        )
        : item.status !== 'closed' && <p className="uat-hint">You can change the status after this item ships to your environment. You can comment now.</p>)}

      {item.cases.length > 0 && (
        <div className="uat-block">
          <h4>Test Cases <span>{uatCaseTally(item.cases)}</span></h4>
          <ol className="uat-cases">
            {item.cases.map((testCase, index) => (
              <li key={testCase.id} className={`uat-case uat-case--${testCase.result}`}>
                <div className="uat-case-text">
                  {testCase.area && <span className="uat-case-area">{testCase.area}</span>}
                  <p>{testCase.text}</p>
                  {testCase.note && <p className="uat-case-note"><strong>Note:</strong> {testCase.note}</p>}
                  {testCase.resultBy && testCase.resultAt && <small>Marked {UAT_CASE_RESULT_LABEL[testCase.result]} by {testCase.resultBy}, {formatUatTime(testCase.resultAt)}</small>}
                </div>
                {canEdit
                  ? (
                    <div className="uat-result" role="group" aria-label={`Result for test case ${index + 1}`}>
                      {UAT_CASE_RESULTS.map((result) => (
                        <button
                          type="button"
                          key={result}
                          className={`uat-result-option uat-result-option--${result}${testCase.result === result ? ' is-selected' : ''}`}
                          aria-pressed={testCase.result === result}
                          disabled={itemBusy}
                          onClick={() => { if (testCase.result !== result || result === 'fail') onResult(item, testCase, result) }}
                        >
                          {UAT_CASE_RESULT_LABEL[result]}
                        </button>
                      ))}
                    </div>
                  )
                  : <StatusBadge tone={testCase.result === 'pass' ? 'success' : testCase.result === 'fail' ? 'danger' : 'neutral'}>{UAT_CASE_RESULT_LABEL[testCase.result]}</StatusBadge>}
              </li>
            ))}
          </ol>
        </div>
      )}

      <div className="uat-block uat-comments">
        <div className="uat-block-head">
          <h4>Comments <span>{threads.length}</span></h4>
          {canEdit && !composing && <Button type="button" variant="secondary" compact disabled={itemBusy} onClick={() => setComposing(true)}><ChatAdd size={14} aria-hidden="true" />Comment</Button>}
        </div>
        {canEdit && composing && (
          <form className="thread-form" onSubmit={(event) => void sendComment(event)} noValidate>
            <Field label="Your Comment" error={error || undefined} hint={`${trimmed.length.toLocaleString('en-US')} of ${UAT_LIMITS.noteChars.toLocaleString('en-US')} characters. The Prentus team gets it right away.`}>
              <textarea id={commentFieldId} value={text} rows={3} maxLength={UAT_LIMITS.noteChars} autoFocus onChange={(event) => setText(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) void sendComment(event) }} />
            </Field>
            <div className="thread-form-actions">
              <Button type="submit" compact disabled={itemBusy}>{itemBusy && <SpinnerGap size={14} className="spin" aria-hidden="true" />}Send Comment</Button>
              <Button type="button" variant="secondary" compact disabled={itemBusy} onClick={() => { setComposing(false); setError('') }}>Cancel</Button>
            </div>
          </form>
        )}
        {threads.length === 0
          ? <p className="uat-comments-empty">{canEdit ? 'No comments yet. Select Comment to ask a question about this item or tell the Prentus team what you found. Your team and Prentus reply here.' : 'No comments yet. Customer comments on this item show here, and both sides reply in the thread.'}</p>
          : (
            <ol className="uat-comment-list">
              {threads.map((entry) => {
                const label = commentLabel(entry)
                return (
                  <li key={entry.id} className="uat-comment">
                    <p className="plan-note-head"><strong>{entry.authorName}</strong>{label && <span className="uat-comment-kind">{label}</span>}{entry.fromItemTitle && <span className="uat-comment-kind">From {entry.fromItemTitle}</span>}<time dateTime={entry.createdAt}>{formatUatTime(entry.createdAt)}</time></p>
                    {entry.caseText && <p className="uat-history-case">Test case: {entry.caseText}</p>}
                    {entry.body && <p className="plan-note-body">{entry.body}</p>}
                    <ThreadReplies replies={entry.replies} label={`${entry.authorName}'s comment on ${item.title}`} canReply={thread.canReply} canModerate={thread.canModerate} assistantPending={thread.pending.has(entry.id)} onReply={(body) => thread.onReply(entry.id, body)} onModerate={thread.onModerate} />
                  </li>
                )
              })}
            </ol>
          )}
      </div>

      {others.length > 0 && (
        <div className="uat-disclosure">
          <button type="button" className="uat-disclosure-toggle" aria-expanded={historyOpen} aria-controls={historyId} onClick={() => setHistoryOpen((value) => !value)}>
            <ChevronDown size={16} aria-hidden="true" className={historyOpen ? 'is-open' : ''} />
            History ({others.length})
          </button>
          {historyOpen && (
            <ol id={historyId} className="uat-history">
              {[...others].reverse().map((entry) => (
                <li key={entry.id} className={`uat-history-entry uat-history-entry--${entry.kind}`}>
                  <ClockCounterClockwise size={16} aria-hidden="true" />
                  <div>
                    <p className="uat-history-line"><strong>{historySentence(entry)}</strong>{entry.fromItemTitle ? ` (on ${entry.fromItemTitle})` : ''} <time dateTime={entry.createdAt}>{formatUatTime(entry.createdAt)}</time></p>
                    {entry.caseText && <p className="uat-history-case">Test case: {entry.caseText}</p>}
                    {entry.body && <blockquote>{entry.body}</blockquote>}
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>
      )}
    </div>
  )
}

const PROMPT_COPY: Record<Prompt['kind'], { title: string; description: string; label: string; submit: string }> = {
  needs_fix: { title: 'Mark as Needs Fix', description: 'Tell the Prentus team what needs a fix. They get it right away, and it shows under Comments.', label: 'What Needs a Fix', submit: 'Mark Needs Fix' },
  fail: { title: 'Mark Test Case as Fail', description: 'Tell the Prentus team what happened. They get it right away, and it shows under Comments.', label: 'What Happened', submit: 'Mark Fail' },
}

function NoteDialog({ prompt, busy, onClose, onSubmit }: { prompt: Prompt | null; busy: boolean; onClose: () => void; onSubmit: (note: string) => Promise<boolean> }) {
  const [text, setText] = useState('')
  const [error, setError] = useState('')
  const formId = useId()
  // A case that already failed opens with its note, so sending it again changes nothing.
  useEffect(() => { setText(prompt?.kind === 'fail' ? prompt.testCase.note || '' : ''); setError('') }, [prompt])
  if (!prompt) return null
  const copy = PROMPT_COPY[prompt.kind]
  const trimmed = text.trim()

  function submit(event: FormEvent) {
    event.preventDefault()
    if (trimmed.length > UAT_LIMITS.noteChars) { setError(`Keep the note under ${UAT_LIMITS.noteChars.toLocaleString('en-US')} characters.`); return }
    setError('')
    void onSubmit(trimmed)
  }

  return (
    <Dialog
      open
      onClose={onClose}
      dismissible={!busy}
      busy={busy}
      title={copy.title}
      description={<>{copy.description} <strong>{prompt.kind === 'fail' ? prompt.testCase.text.split('\n')[0] : prompt.item.title}</strong></>}
      footer={(
        <>
          <Button type="button" variant="secondary" disabled={busy} onClick={onClose}>Cancel</Button>
          <Button type="submit" form={formId} disabled={busy}>{busy && <SpinnerGap size={16} className="spin" aria-hidden="true" />}{copy.submit}</Button>
        </>
      )}
    >
      <form id={formId} onSubmit={submit} noValidate>
        <Field label={copy.label} error={error || undefined} hint={`${trimmed.length.toLocaleString('en-US')} of ${UAT_LIMITS.noteChars.toLocaleString('en-US')} characters. Optional.`}>
          <textarea value={text} rows={5} maxLength={UAT_LIMITS.noteChars} data-autofocus onChange={(event) => setText(event.target.value)} />
        </Field>
      </form>
    </Dialog>
  )
}

const TYPE_HINT: Record<UatRequestType, string> = {
  issue_found: 'Something does not work as it should. Tell us the steps and what you saw.',
  question: 'You want to know how something should work.',
  request: 'Something new or different you want in the product.',
}

/** Add Item (Rod, 2026-11-04): a customer adds an Issue Found, a Question or a Request. */
function AddItemDialog({ open, projects, items, customerLabel, onClose, onSubmit }: {
  open: { projectId: string | null } | null
  projects: UatProject[]
  /** For the default type of a Question: the type of the project's items. */
  items: UatItem[]
  customerLabel: string
  onClose: () => void
  onSubmit: (input: { type: UatRequestType; itemType: UatItemType; title: string; details: string; projectId: string | null }) => Promise<boolean>
}) {
  const formId = useId()
  const typeName = useId()
  const itemTypeName = useId()
  const [type, setType] = useState<UatRequestType>('issue_found')
  // The item type follows the default for the request type and project until the customer picks one (Rod, 2026-11-04).
  const [chosenItemType, setChosenItemType] = useState<UatItemType | null>(null)
  const [title, setTitle] = useState('')
  const [details, setDetails] = useState('')
  const [projectId, setProjectId] = useState('')
  const [titleError, setTitleError] = useState('')
  const [busy, setBusy] = useState(false)
  useEffect(() => {
    if (!open) return
    setType('issue_found'); setTitle(''); setDetails(''); setTitleError(''); setChosenItemType(null)
    setProjectId(open.projectId || '')
  }, [open])
  if (!open) return null
  const requests = projects.find((project) => project.isRequests)
  const contextProject = projectId || requests?.id || null
  const itemType = chosenItemType || defaultUatItemType(type, contextProject ? items.filter((item) => item.projectId === contextProject) : [])

  async function submit(event: FormEvent) {
    event.preventDefault()
    if (busy) return
    const cleanTitle = title.trim()
    if (!cleanTitle) { setTitleError('Give the item a title.'); return }
    if (cleanTitle.length > UAT_LIMITS.titleChars) { setTitleError(`Keep the title under ${UAT_LIMITS.titleChars} characters.`); return }
    setTitleError('')
    setBusy(true)
    await onSubmit({ type, itemType, title: cleanTitle, details: details.trim(), projectId: projectId || null })
    setBusy(false)
  }

  return (
    <Dialog
      open
      onClose={onClose}
      dismissible={!busy}
      busy={busy}
      title="Add Item"
      description={`The item shows as Added by ${customerLabel}, and the Prentus team gets it right away as a ticket. You and Prentus talk about it under Comments.`}
      footer={(
        <>
          <Button type="button" variant="secondary" disabled={busy} onClick={onClose}>Cancel</Button>
          <Button type="submit" form={formId} disabled={busy}>{busy && <SpinnerGap size={16} className="spin" aria-hidden="true" />}Add Item</Button>
        </>
      )}
    >
      <form id={formId} className="uat-add-form" onSubmit={(event) => void submit(event)} noValidate>
        <fieldset className="uat-type-picker">
          <legend>What You Are Adding</legend>
          <div className="uat-type-options">
            {UAT_REQUEST_TYPES.map((option) => (
              <label key={option} className={`uat-type-option${type === option ? ' is-selected' : ''}`}>
                <input type="radio" name={typeName} value={option} checked={type === option} onChange={() => setType(option)} />
                <span>{UAT_REQUEST_TYPE_LABEL[option]}</span>
              </label>
            ))}
          </div>
          <p className="uat-type-hint">{TYPE_HINT[type]}</p>
        </fieldset>
        <fieldset className="uat-type-picker">
          <legend>Item Type</legend>
          <div className="uat-type-options uat-item-type-options">
            {UAT_ITEM_TYPES.map((option) => (
              <label key={option} className={`uat-type-option uat-item-type-option${itemType === option ? ' is-selected' : ''}`}>
                <input type="radio" name={itemTypeName} value={option} checked={itemType === option} onChange={() => setChosenItemType(option)} />
                <strong>{UAT_ITEM_TYPE_LABEL[option]}</strong>
                <small>{UAT_ITEM_TYPE_HINT[option]}</small>
              </label>
            ))}
          </div>
        </fieldset>
        <Field label="Title" required error={titleError || undefined}>
          <Input value={title} maxLength={UAT_LIMITS.titleChars} data-autofocus onChange={(event) => setTitle(event.target.value)} placeholder={type === 'issue_found' ? 'For example: Saved filters reset on a phone' : type === 'question' ? 'For example: Can advisors see student resumes?' : 'For example: Export the advisor queue'} />
        </Field>
        <Field label="Details" hint={`${details.trim().length.toLocaleString('en-US')} of ${UAT_LIMITS.noteChars.toLocaleString('en-US')} characters. Optional.`}>
          <textarea value={details} rows={4} maxLength={UAT_LIMITS.noteChars} onChange={(event) => setDetails(event.target.value)} />
        </Field>
        <Field label="Project" hint={requests ? 'Optional. Without a project, it goes to Your Requests.' : 'Optional. Without a project, it goes to a new Your Requests project.'}>
          <Select value={projectId} onChange={(event) => setProjectId(event.target.value)}>
            <option value="">No Project</option>
            {projects.map((project) => <option key={project.id} value={project.id}>{project.name}</option>)}
          </Select>
        </Field>
      </form>
    </Dialog>
  )
}

